import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import { settingsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import {
  Plus, Search, Filter, Edit2, Trash2, X,
  Package, Tag, Clock, CheckCircle,
  XCircle, Save, Layers
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminOrders.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const MATERIAL_TYPES = ['FDM', 'SLA', 'SLS', 'Other'];
const STOCK_OPTIONS = [
  { value: 'in_stock',      label: 'Disponibile',  color: '#10b981', bg: '#10b98120' },
  { value: 'low_stock',     label: 'Scorte basse', color: '#f59e0b', bg: '#f59e0b20' },
  { value: 'out_of_stock',  label: 'Esaurito',     color: '#ef4444', bg: '#ef444420' },
];

const DEFAULT_MATERIALS = [
  { id: 'mat_pla',       name: 'PLA Standard',    type: 'FDM', colors: ['Bianco','Nero','Grigio','Rosso','Blu','Verde','Giallo'],   pricePerGram: 0.08,  minimumWeight: 5,  stock: 'in_stock',      leadTime: 2, active: true,  description: '' },
  { id: 'mat_petg',      name: 'PETG',            type: 'FDM', colors: ['Bianco','Nero','Trasparente','Rosso'],                     pricePerGram: 0.12,  minimumWeight: 5,  stock: 'in_stock',      leadTime: 3, active: true,  description: '' },
  { id: 'mat_abs',       name: 'ABS',             type: 'FDM', colors: ['Nero','Bianco','Grigio'],                                  pricePerGram: 0.10,  minimumWeight: 10, stock: 'in_stock',      leadTime: 3, active: true,  description: '' },
  { id: 'mat_tpu',       name: 'TPU Flessibile',  type: 'FDM', colors: ['Nero','Bianco','Blu'],                                     pricePerGram: 0.18,  minimumWeight: 5,  stock: 'low_stock',     leadTime: 4, active: true,  description: '' },
  { id: 'mat_resin_std', name: 'Resina Standard', type: 'SLA', colors: ['Bianco','Grigio','Trasparente'],                           pricePerGram: 0.20,  minimumWeight: 3,  stock: 'in_stock',      leadTime: 2, active: true,  description: '' },
  { id: 'mat_nylon',     name: 'Nylon PA12',      type: 'SLS', colors: ['Bianco','Grigio'],                                         pricePerGram: 0.45,  minimumWeight: 20, stock: 'out_of_stock',  leadTime: 7, active: false, description: '' },
];

const BLANK_MATERIAL = {
  id: '',
  name: '',
  type: 'FDM',
  colors: [],
  pricePerGram: 0.08,
  minimumWeight: 5,
  stock: 'in_stock',
  description: '',
  leadTime: 2,
  active: true,
};

// ─── Helper components ─────────────────────────────────────────────────────────

const StockBadge = ({ stock }) => {
  const { t } = useTranslation();
  const opt = STOCK_OPTIONS.find(o => o.value === stock) || STOCK_OPTIONS[0];
  return (
    <span style={{
      padding: '0.3rem 0.7rem',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: 700,
      color: opt.color,
      background: opt.bg,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
    }}>
      {t('admin.products.stock.' + opt.value)}
    </span>
  );
};

const ActiveBadge = ({ active }) => {
  const { t } = useTranslation();
  return (
    <span style={{
      padding: '0.3rem 0.7rem',
      borderRadius: '20px',
      fontSize: '0.72rem',
      fontWeight: 700,
      color:      active ? '#10b981' : '#71717a',
      background: active ? '#10b98118' : '#71717a18',
      whiteSpace: 'nowrap',
    }}>
      {active ? t('admin.products.active') : t('admin.products.inactive')}
    </span>
  );
};

// ─── Modal ─────────────────────────────────────────────────────────────────────

const MaterialModal = ({ material, onClose, onSave, saving }) => {
  const { t } = useTranslation();
  const [form, setForm]       = useState(() => ({ ...BLANK_MATERIAL, ...material }));
  const [colorInput, setColorInput] = useState('');
  const colorRef = useRef(null);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const addColor = () => {
    const c = colorInput.trim();
    if (c && !form.colors.includes(c)) {
      set('colors', [...form.colors, c]);
    }
    setColorInput('');
    colorRef.current?.focus();
  };

  const removeColor = (c) => set('colors', form.colors.filter(x => x !== c));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addColor(); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="order-create-modal"
        style={{
          background: '#18181b',
          border: '1px solid #27272a',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.5rem', borderBottom: '1px solid #27272a',
        }}>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Layers size={20} color="#10b981" />
            {material.id ? t('admin.products.editTitle') : t('admin.products.newTitle')}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: '4px' }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable body */}
        <form id="mat-modal-form" onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* Row 1: Nome + Tipo */}
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 160px' }}>
            <div className="form-group">
              <label>{t('admin.products.modalName')}</label>
              <input
                className="admin-input"
                required
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="es. PLA Pro"
              />
            </div>
            <div className="form-group">
              <label>{t('admin.products.modalType')}</label>
              <select className="admin-input" value={form.type} onChange={e => set('type', e.target.value)}>
                {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Prezzo + Peso min + Lead time */}
          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="form-group">
              <label>{t('admin.products.modalPricePerGram')}</label>
              <input
                className="admin-input"
                type="number" step="0.001" min="0" required
                value={form.pricePerGram}
                onChange={e => set('pricePerGram', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="form-group">
              <label>{t('admin.products.modalMinWeight')}</label>
              <input
                className="admin-input"
                type="number" min="1" step="1" required
                value={form.minimumWeight}
                onChange={e => set('minimumWeight', parseInt(e.target.value, 10) || 1)}
              />
            </div>
            <div className="form-group">
              <label>{t('admin.products.modalLeadTime')}</label>
              <input
                className="admin-input"
                type="number" min="1" step="1" required
                value={form.leadTime}
                onChange={e => set('leadTime', parseInt(e.target.value, 10) || 1)}
              />
            </div>
          </div>

          {/* Row 3: Stock + Attivo */}
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 140px' }}>
            <div className="form-group">
              <label>{t('admin.products.modalStockStatus')}</label>
              <select className="admin-input" value={form.stock} onChange={e => set('stock', e.target.value)}>
                {STOCK_OPTIONS.map(o => <option key={o.value} value={o.value}>{t('admin.products.stock.' + o.value)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('admin.products.modalActive')}</label>
              <button
                type="button"
                onClick={() => set('active', !form.active)}
                style={{
                  height: '42px',
                  borderRadius: '8px',
                  border: `1px solid ${form.active ? '#10b981' : '#27272a'}`,
                  background: form.active ? '#10b98120' : '#18181b',
                  color: form.active ? '#10b981' : '#71717a',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                }}
              >
                {form.active
                  ? <><CheckCircle size={15} /> {t('admin.products.active')}</>
                  : <><XCircle size={15} /> {t('admin.products.inactive')}</>
                }
              </button>
            </div>
          </div>

          {/* Colors */}
          <div className="form-group">
            <label>{t('admin.products.modalColors')}</label>
            <div style={{
              background: '#0a0a0a',
              border: '1px solid #27272a',
              borderRadius: '8px',
              padding: '0.75rem',
              minHeight: '56px',
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: form.colors.length ? '0.5rem' : 0 }}>
                {form.colors.map(c => (
                  <span
                    key={c}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      background: '#27272a', color: '#e4e4e7',
                      borderRadius: '6px', padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem', fontWeight: 500,
                    }}
                  >
                    {c}
                    <button
                      type="button"
                      onClick={() => removeColor(c)}
                      style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  ref={colorRef}
                  className="admin-input"
                  style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                  placeholder={t('admin.products.modalAddColor')}
                  value={colorInput}
                  onChange={e => setColorInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  onClick={addColor}
                  style={{
                    background: '#10b98120', border: '1px solid #10b981',
                    color: '#10b981', borderRadius: '8px',
                    padding: '0.5rem 0.75rem', cursor: 'pointer',
                    fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center',
                  }}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label>{t('admin.products.modalDescription')}</label>
            <textarea
              className="admin-input"
              rows={3}
              style={{ resize: 'vertical' }}
              placeholder={t('admin.products.modalDescriptionPlaceholder')}
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="admin-btn"
            style={{ background: '#27272a', color: '#a1a1aa', border: 'none' }}
          >
            {t('admin.products.modalCancel')}
          </button>
          <button
            type="submit"
            form="mat-modal-form"
            className="admin-btn admin-btn-primary"
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            {saving ? t('admin.products.modalSaving') : <><Save size={16} /> {t('admin.products.modalSave')}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

const AdminProducts = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [materials, setMaterials]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [editTarget, setEditTarget]   = useState(null);   // null = new, object = edit
  const [deleteConfirm, setDeleteConfirm] = useState(null); // id pending delete

  // Filters
  const [search, setSearch]           = useState('');
  const [typeFilter, setTypeFilter]   = useState('');
  const [stockFilter, setStockFilter] = useState('');

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getAll({ category: 'materials' });
      const raw = res.data?.settings?.[0]?.settings?.materials;
      if (Array.isArray(raw) && raw.length > 0) {
        setMaterials(raw);
      } else {
        // First run: seed defaults and persist them
        setMaterials(DEFAULT_MATERIALS);
        await settingsAPI.update('materials', { materials: DEFAULT_MATERIALS });
      }
    } catch (err) {
      console.error('Error loading materials:', err);
      toast.error(t('admin.products.errorLoad'));
      setMaterials(DEFAULT_MATERIALS);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  // ── Persist helper ────────────────────────────────────────────────────────

  const persist = async (newList) => {
    await settingsAPI.update('materials', { materials: newList });
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const openNew = () => {
    setEditTarget({ ...BLANK_MATERIAL });
    setShowModal(true);
  };

  const openEdit = (mat) => {
    setEditTarget({ ...mat });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditTarget(null);
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      let updated;
      if (form.id && materials.some(m => m.id === form.id)) {
        // Edit
        updated = materials.map(m => m.id === form.id ? { ...form } : m);
        toast.success(t('admin.products.successUpdate', { name: form.name }));
      } else {
        // Add new
        const newMat = { ...form, id: `mat_${Date.now()}` };
        updated = [...materials, newMat];
        toast.success(t('admin.products.successAdd', { name: form.name }));
      }
      setMaterials(updated);         // optimistic
      await persist(updated);
      closeModal();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(t('admin.products.errorSave'));
      await loadMaterials();         // revert
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const mat = materials.find(m => m.id === id);
    const updated = materials.filter(m => m.id !== id);
    setMaterials(updated);           // optimistic
    setDeleteConfirm(null);
    try {
      await persist(updated);
      toast.success(t('admin.products.successDelete', { name: mat?.name }));
    } catch (err) {
      toast.error(t('admin.products.errorDelete'));
      await loadMaterials();
    }
  };

  // ── Filtered list ─────────────────────────────────────────────────────────

  const filtered = materials.filter(m => {
    const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase());
    const matchType   = !typeFilter  || m.type  === typeFilter;
    const matchStock  = !stockFilter || m.stock === stockFilter;
    return matchSearch && matchType && matchStock;
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        {/* Header */}
        <header className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Layers size={28} color="#10b981" /> {t('admin.products.title')}
            </h1>
            <p>{t('admin.products.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <LanguageToggle />
            <button className="admin-btn admin-btn-primary" onClick={openNew}>
              <Plus size={16} /> {t('admin.products.new')}
            </button>
          </div>
        </header>

        {/* Stats strip */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {[
            { label: t('admin.products.statsTotal'),       value: materials.length,                                              color: '#a1a1aa' },
            { label: t('admin.products.statsAvailable'),   value: materials.filter(m => m.stock === 'in_stock').length,          color: '#10b981' },
            { label: t('admin.products.statsLowStock'),    value: materials.filter(m => m.stock === 'low_stock').length,         color: '#f59e0b' },
            { label: t('admin.products.statsOutOfStock'),  value: materials.filter(m => m.stock === 'out_of_stock').length,      color: '#ef4444' },
            { label: t('admin.products.statsInactive'),    value: materials.filter(m => !m.active).length,                      color: '#71717a' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#18181b', border: '1px solid #27272a',
              borderRadius: '10px', padding: '0.75rem 1.25rem',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: s.color }}>{s.value}</span>
              <span style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div style={{
          display: 'flex', gap: '0.75rem', flexWrap: 'wrap',
          marginBottom: '1.5rem', alignItems: 'center',
        }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} />
            <input
              className="admin-input"
              style={{ paddingLeft: '2.25rem', width: '100%' }}
              placeholder={t('admin.products.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Type filter */}
          <div style={{ position: 'relative' }}>
            <Filter size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} />
            <select
              className="admin-input"
              style={{ paddingLeft: '2.25rem', minWidth: '130px' }}
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
            >
              <option value="">{t('admin.products.allTypes')}</option>
              {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {/* Stock filter */}
          <div style={{ position: 'relative' }}>
            <Package size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#71717a' }} />
            <select
              className="admin-input"
              style={{ paddingLeft: '2.25rem', minWidth: '160px' }}
              value={stockFilter}
              onChange={e => setStockFilter(e.target.value)}
            >
              <option value="">{t('admin.products.allStock')}</option>
              {STOCK_OPTIONS.map(o => <option key={o.value} value={o.value}>{t('admin.products.stock.' + o.value)}</option>)}
            </select>
          </div>

          {/* Clear */}
          {(search || typeFilter || stockFilter) && (
            <button
              onClick={() => { setSearch(''); setTypeFilter(''); setStockFilter(''); }}
              style={{
                background: 'none', border: '1px solid #27272a',
                color: '#71717a', borderRadius: '8px',
                padding: '0.6rem 0.8rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem',
              }}
            >
              <X size={13} /> Reset
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#71717a' }}>
            <Package size={40} style={{ marginBottom: '1rem', opacity: 0.4 }} />
            <p>{t('admin.products.loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          /* Empty state */
          <div style={{
            background: '#18181b', border: '1px solid #27272a',
            borderRadius: '12px', padding: '4rem',
            textAlign: 'center',
          }}>
            {materials.length === 0 ? (
              <>
                <Layers size={56} color="#10b981" style={{ marginBottom: '1rem', opacity: 0.6 }} />
                <h3 style={{ color: '#fff', margin: '0 0 0.5rem' }}>{t('admin.products.emptyTitle')}</h3>
                <p style={{ color: '#71717a', marginBottom: '1.5rem' }}>{t('admin.products.emptySubtitle')}</p>
                <button className="admin-btn admin-btn-primary" onClick={openNew}>
                  <Plus size={16} /> {t('admin.products.emptyBtn')}
                </button>
              </>
            ) : (
              <>
                <Search size={40} style={{ marginBottom: '1rem', opacity: 0.3, color: '#a1a1aa' }} />
                <p style={{ color: '#71717a' }}>{t('admin.products.emptyFilter')}</p>
              </>
            )}
          </div>
        ) : (
          <div style={{
            background: '#18181b', border: '1px solid #27272a',
            borderRadius: '12px', overflow: 'hidden',
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#0a0a0a', borderBottom: '1px solid #27272a' }}>
                    {[t('admin.products.tableColName'), t('admin.products.tableColType'), t('admin.products.tableColPrice'), t('admin.products.tableColStock'), t('admin.products.tableColColors'), t('admin.products.tableColLeadTime'), t('admin.products.tableColStatus'), t('admin.products.tableColActions')].map(h => (
                      <th key={h} style={{
                        padding: '0.875rem 1rem', textAlign: 'left',
                        color: '#71717a', fontWeight: 600, fontSize: '0.75rem',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((mat, idx) => (
                    <tr
                      key={mat.id}
                      style={{
                        borderBottom: idx < filtered.length - 1 ? '1px solid #27272a' : 'none',
                        transition: 'background 0.15s',
                        opacity: mat.active ? 1 : 0.55,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#1f1f22'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Nome */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <span style={{ color: '#fff', fontWeight: 600 }}>{mat.name}</span>
                        {mat.description && (
                          <div style={{ color: '#71717a', fontSize: '0.75rem', marginTop: '2px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {mat.description}
                          </div>
                        )}
                      </td>

                      {/* Tipo */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <span style={{
                          background: '#27272a', color: '#a1a1aa',
                          borderRadius: '6px', padding: '0.25rem 0.6rem',
                          fontWeight: 600, fontSize: '0.75rem',
                        }}>
                          {mat.type}
                        </span>
                      </td>

                      {/* Prezzo/g */}
                      <td style={{ padding: '0.875rem 1rem', color: '#10b981', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        €{Number(mat.pricePerGram).toFixed(3)}
                      </td>

                      {/* Stock */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <StockBadge stock={mat.stock} />
                      </td>

                      {/* Colori (badge count) */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          background: '#27272a', color: '#e4e4e7',
                          borderRadius: '6px', padding: '0.25rem 0.6rem',
                          fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          <Tag size={11} />
                          {mat.colors?.length ?? 0}
                        </span>
                      </td>

                      {/* Lead time */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#a1a1aa' }}>
                          <Clock size={13} />
                          {mat.leadTime}g
                        </span>
                      </td>

                      {/* Stato */}
                      <td style={{ padding: '0.875rem 1rem' }}>
                        <ActiveBadge active={mat.active} />
                      </td>

                      {/* Azioni */}
                      <td style={{ padding: '0.875rem 1rem', whiteSpace: 'nowrap' }}>
                        {deleteConfirm === mat.id ? (
                          /* Inline delete confirmation */
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ color: '#ef4444', fontSize: '0.78rem', fontWeight: 600 }}>{t('admin.products.confirmDelete')}</span>
                            <button
                              onClick={() => handleDelete(mat.id)}
                              style={{
                                background: '#ef444420', border: '1px solid #ef4444',
                                color: '#ef4444', borderRadius: '6px',
                                padding: '0.25rem 0.6rem', cursor: 'pointer',
                                fontSize: '0.75rem', fontWeight: 700,
                              }}
                            >
                              {t('admin.products.confirmYes')}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              style={{
                                background: '#27272a', border: 'none',
                                color: '#a1a1aa', borderRadius: '6px',
                                padding: '0.25rem 0.6rem', cursor: 'pointer',
                                fontSize: '0.75rem', fontWeight: 700,
                              }}
                            >
                              {t('admin.products.confirmNo')}
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            <button
                              onClick={() => openEdit(mat)}
                              className="action-btn action-btn-edit"
                              style={{ padding: '0.4rem 0.65rem', borderRadius: '7px', fontSize: '0.78rem', flex: 'unset' }}
                              title={t('admin.products.tooltipEdit')}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(mat.id)}
                              style={{
                                background: '#ef444420', color: '#ef4444',
                                border: 'none', borderRadius: '7px',
                                padding: '0.4rem 0.65rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center',
                                transition: 'background 0.2s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#ef444430'}
                              onMouseLeave={e => e.currentTarget.style.background = '#ef444420'}
                              title={t('admin.products.tooltipDelete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table footer */}
            <div style={{
              padding: '0.75rem 1rem',
              borderTop: '1px solid #27272a',
              color: '#71717a',
              fontSize: '0.78rem',
            }}>
              {filtered.length === 1 ? t('admin.products.tableFooter', { count: filtered.length }) : t('admin.products.tableFooterPlural', { count: filtered.length })}{(search || typeFilter || stockFilter) && ' ' + t('admin.products.tableFooterOf', { total: materials.length })}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && editTarget !== null && (
        <MaterialModal
          material={editTarget}
          onClose={closeModal}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
};

export default AdminProducts;
